import { Ref, ref } from "vue";
import { ResponsiveUtils } from "./responsive-utils";

type BreakpointType = keyof Omit<typeof ResponsiveUtils.breakpoints, "xs">;
type ReactiveBreakpoint = {
    key: BreakpointType;
    minSize: number;
    isInUse: boolean;
    value: Ref<boolean>;
};

const reactiveBreakpoints: ReactiveBreakpoint[] = Object.keys(ResponsiveUtils.breakpoints)
    .filter((x) => x !== "xs")
    .map((x) => ({
        isInUse: false,
        key: x as BreakpointType,
        minSize: ResponsiveUtils.breakpoints[x],
        value: ref(false),
    }));

/**
 * Returns ref with boolean value, which means, current breakpoint is same or bigger
 * eg. media min-width: x
 * since its breakpoint up, it doesnt make sense for 'xs' which would be always true, so its omited
 * @param breakpoints breakpoint name eg. 'xs2' or 'xl'
 */
export function useBreakpoints(breakpoints: BreakpointType): Ref<boolean>;

/**
 * Returns object, with keys same as parameter value, their value is ref with boolean value, which means, current breakpoint is same or bigger
 * eg. media min-width: x
 * Example of return value: { sm: ref(true), md: ref(false) }
 * Returns only value passed in parametrs,
 * since its breakpoint up, it doesnt make sense for 'xs' which would be always true, so its omited
 * @param breakpoints all breakpoint names eg. 'xs2' or 'xl'
 */
export function useBreakpoints(...breakpoints: BreakpointType[]): { [key in BreakpointType]: Ref<boolean> };

export function useBreakpoints(...breakpoints: BreakpointType[]) {
    if (breakpoints?.length == 0) return;

    if (breakpoints.length > 1) {
        return breakpoints.reduce((acc, x) => {
            acc[x] = reactiveBreakpoint(x);
            return acc;
        }, {});
    } else {
        return reactiveBreakpoint(breakpoints[0]);
    }
}

function reactiveBreakpoint(breakpoint: BreakpointType) {
    const brkpnt = reactiveBreakpoints.find((x) => x.key === breakpoint);
    if (!brkpnt.isInUse) {
        brkpnt.isInUse = true;
        const media = window.matchMedia(`(min-width: ${brkpnt.minSize}px)`);
        brkpnt.value.value = media.matches;
        media.onchange = (e) => {
            brkpnt.value.value = e.matches;
        };
    }
    return brkpnt.value;
}
